<script>
import { useAlert } from 'dashboard/composables';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';

const COLUMNS = [
  { col: 'A', header: 'treatment_name', label: 'Treatment Name', required: true },
  { col: 'B', header: 'general_terms',  label: 'General Terms',  required: false },
  { col: 'C', header: 'description',    label: 'Description',    required: false },
  { col: 'D', header: 'benefits',       label: 'Benefits',       required: false },
  { col: 'E', header: 'concerns',       label: 'Concerns',       required: false },
  { col: 'F', header: 'considerations', label: 'Considerations', required: false },
  { col: 'G', header: 'price',          label: 'Price',          required: false },
  { col: 'H', header: 'follow_up',      label: 'Follow Up',      required: false },
  { col: 'I', header: 'image_links',    label: 'Image Links (comma-separated Google Drive URLs)', required: false },
];

export default {
  name: "UploadServicesModal",
  components: { BaseModal },
  props: {
    branchId: {
      type: [String, Number],
      default: null,
    },
  },
  emits: ['close', 'refresh'],
  data() {
    return {
      columns: COLUMNS,
      payload: { file: null },
      fileName: null,
      errMsg: '',
      isUploading: false,
      uploadProgress: null,
    };
  },
  computed: {
    confirmButtonText() {
      return this.isUploading ? 'Uploading...' : 'Upload Services';
    },
  },
  methods: {
    isValidFile(file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];
      const validExtensions = ['.csv', '.xls', '.xlsx'];
      return (
        validTypes.includes(file.type) ||
        validExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
      );
    },

    handleFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        if (!this.isValidFile(file)) {
          this.errMsg = 'Only CSV or Excel files are allowed';
          this.payload.file = null;
          this.fileName = null;
          return;
        }
        if (file.size > 10 * 1024 * 1024) {
          this.errMsg = 'File size must be less than 10MB';
          this.payload.file = null;
          this.fileName = null;
          return;
        }
        this.errMsg = '';
        this.payload.file = file;
        this.fileName = file.name;
      }
    },

    handleDrop(e) {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) {
        if (!this.isValidFile(file)) {
          this.errMsg = 'Only CSV or Excel files are allowed';
          return;
        }
        if (file.size > 10 * 1024 * 1024) {
          this.errMsg = 'File size must be less than 10MB';
          return;
        }
        this.errMsg = '';
        this.payload.file = file;
        this.fileName = file.name;
      }
    },

    removeFile() {
      this.payload.file = null;
      this.fileName = null;
      this.errMsg = '';
      if (this.$refs.fileInput) this.$refs.fileInput.value = null;
    },

    downloadSample() {
      const headers = COLUMNS.map(c => c.header);
      const example = [
        'Deep Tissue Massage',
        'Standard terms apply',
        'A therapeutic massage targeting deep muscle layers',
        'Relieves chronic pain, reduces tension',
        'May cause temporary soreness',
        'Not suitable for clients with blood clots',
        '120.00',
        'Drink plenty of water after session',
        'https://drive.google.com/file/d/example1.jpg,https://drive.google.com/file/d/example2.png',
      ];
      const csvContent = [headers.join(','), example.map(v => `"${v}"`).join(',')].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'services_sample.csv';
      a.click();
      URL.revokeObjectURL(url);
    },

    async handleConfirm() {
      if (!this.payload.file) {
        useAlert('Please upload a CSV or Excel file before submitting.');
        this.errMsg = 'File is required';
        return;
      }

      this.isUploading = true;
      try {
        const payload = {
          file: this.payload.file,
          branch_id: this.branchId || null,
        };

        const res = await this.$store.dispatch('serviceV2/importServices', payload);

        const jobId = res.job_id || res.data.job_id;
        this.$store.dispatch('serviceV2/getImportStatus', jobId);
        useAlert(`Import started! Tracking job ID: ${jobId}`);
        this.$emit('close');
      } catch (err) {
        console.error('Upload error:', err);
        const msg =
          Array.isArray(err) ? err.join('\n') :
          err?.message ? `Upload failed: ${err.message}` :
          'Failed to upload services. Please check your file format and try again.';
        useAlert(msg);
      } finally {
        this.isUploading = false;
      }
    },
  },
};
</script>

<template>
  <BaseModal @close="$emit('close')">
    <template #title>
      Upload Services File
    </template>

    <form @submit.prevent="handleConfirm" class="space-y-4">
      <!-- Drop zone -->
      <div>
        <label class="block mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">
          Upload Services File
        </label>
        <div
          class="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
                hover:border-woot-500 dark:hover:border-woot-400
                transition mb-2 bg-white dark:bg-slate-700/50
                border-slate-300 dark:border-slate-600
                hover:bg-slate-50 dark:hover:bg-slate-700/70"
          @dragover.prevent
          @drop="handleDrop"
          @click="$refs.fileInput.click()"
        >
          <input
            ref="fileInput"
            type="file"
            accept=".csv, .xls, .xlsx"
            class="hidden"
            @change="handleFileChange"
          />

          <div v-if="fileName" class="py-2">
            <div class="flex items-center justify-center gap-3">
              <svg class="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div class="text-left">
                <p class="text-slate-700 dark:text-slate-200 font-medium">{{ fileName }}</p>
                <p class="text-xs text-slate-500 dark:text-slate-400">
                  {{ (payload.file.size / 1024 / 1024).toFixed(2) }} MB
                </p>
              </div>
              <woot-button
                size="small"
                icon="delete"
                color-scheme="alert"
                @click.stop="removeFile"
                class="ml-2 hover:bg-red-100 dark:hover:bg-red-500/30"
              />
            </div>
          </div>

          <div v-else class="py-4">
            <svg class="w-12 h-12 mx-auto text-slate-400 dark:text-slate-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
            </svg>
            <p class="text-slate-600 dark:text-slate-300 mb-1">
              Drag & drop your file here, or <span class="text-woot-600 dark:text-woot-400 font-medium">browse</span>
            </p>
            <p class="text-xs text-slate-400 dark:text-slate-500">
              Supported formats: CSV, XLS, XLSX (Max 10MB)
            </p>
          </div>
        </div>

        <p v-if="errMsg" class="text-red-500 text-sm mt-2 flex items-center gap-1">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
          </svg>
          {{ errMsg }}
        </p>
      </div>

      <!-- Upload progress -->
      <div v-if="uploadProgress !== null" class="mt-4">
        <div class="w-full bg-slate-200 dark:bg-slate-600 rounded-full h-2 overflow-hidden">
          <div
            class="bg-woot-500 h-2 transition-all duration-300"
            :style="{ width: uploadProgress + '%' }"
          ></div>
        </div>
        <p class="text-xs text-slate-500 dark:text-slate-400 mt-1 text-center">
          Import progress: {{ uploadProgress.toFixed(0) }}%
        </p>
      </div>

      <!-- Column Reference -->
      <div class="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 text-sm">
        <div class="flex items-center justify-between mb-3">
          <h4 class="font-medium text-slate-700 dark:text-slate-300">Column Reference</h4>
          <button
            type="button"
            class="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-woot-50 dark:bg-woot-900/30 text-woot-600 dark:text-woot-400 hover:bg-woot-100 dark:hover:bg-woot-900/50 transition"
            @click="downloadSample"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
            </svg>
            Download Sample
          </button>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-xs text-left">
            <thead>
              <tr class="border-b border-slate-200 dark:border-slate-700">
                <th class="py-1.5 pr-3 font-semibold text-slate-600 dark:text-slate-400 w-8">Col</th>
                <th class="py-1.5 pr-3 font-semibold text-slate-600 dark:text-slate-400">Field</th>
                <th class="py-1.5 font-semibold text-slate-600 dark:text-slate-400">Required</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="col in columns"
                :key="col.col"
                class="border-b border-slate-100 dark:border-slate-700/50 last:border-0"
              >
                <td class="py-1.5 pr-3 font-mono font-semibold text-slate-500 dark:text-slate-400">{{ col.col }}</td>
                <td class="py-1.5 pr-3 text-slate-700 dark:text-slate-300">{{ col.label }}</td>
                <td class="py-1.5">
                  <span v-if="col.required" class="text-red-500 font-medium">Required</span>
                  <span v-else class="text-slate-400">Optional</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Image format reminder -->
        <div class="mt-3 flex items-start gap-2 p-2.5 rounded-md border border-amber-200 dark:border-amber-700/40">
          <svg class="w-4 h-4 text-amber-500 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
          </svg>
          <p class="text-xs text-amber-700 dark:text-amber-400">
            Column I — Image Links: Only JPG/PNG images are supported. Please ensure all linked Google Drive images are in JPG or PNG format before importing.
          </p>
        </div>

        <!-- File requirements -->
        <ul class="text-slate-600 dark:text-slate-400 space-y-1 text-xs mt-3">
          <li class="flex items-center gap-2">
            <svg class="w-4 h-4 text-green-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
            </svg>
            File must be in CSV, XLS, or XLSX format · Max 10MB
          </li>
          <li class="flex items-center gap-2">
            <svg class="w-4 h-4 text-green-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
            </svg>
            First row must contain column headers exactly as shown above
          </li>
          <li class="flex items-center gap-2">
            <svg class="w-4 h-4 text-green-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
            </svg>
            Multiple image links in column I should be comma-separated
          </li>
        </ul>
      </div>
    </form>

    <template #footer>
      <div class="flex justify-end gap-3 mt-4">
        <woot-button
          color-scheme="secondary"
          @click="$emit('close')"
          :disabled="isUploading"
        >
          Cancel
        </woot-button>
        <woot-button
          @click="handleConfirm"
          :disabled="isUploading || !payload.file"
          :loading="isUploading"
        >
          {{ confirmButtonText }}
        </woot-button>
      </div>
    </template>
  </BaseModal>
</template>
